/**
 * The profile publish rules, mirrored from the database (migration
 * 20261030115142_profile_publish_hide_history.sql). The SERVER is the authority:
 * flat.profile_missing_requirements() decides, the triggers publish / take offline, and
 * publish_my_profile() returns what is still missing. This mirror exists so the UI can say
 * "what is missing" before a round trip and label the server's answer — it never gates anything
 * (Conv 29).
 */

export const PROFILE_STATUSES = ['unpublished', 'published', 'hidden'] as const;
export type ProfileStatus = (typeof PROFILE_STATUSES)[number];

/** English values (Conv 1) — the CHECK on tenant_profiles.hidden_reason and hide_my_profile(). */
export const HIDE_REASONS = [
  'rented_on_flat_hero',
  'rented_outside_flat_hero',
  'not_looking_for_now',
  'skipped',
] as const;
export type HideReason = (typeof HIDE_REASONS)[number];

/** In the order the server reports them. */
export const PUBLISH_REQUIREMENTS = ['photo', 'bio', 'id'] as const;
export type PublishRequirement = (typeof PUBLISH_REQUIREMENTS)[number];

export const VISIBILITY_EVENTS = ['published_auto', 'published_manual', 'unhidden', 'hidden', 'offline_auto'] as const;
export type VisibilityEvent = (typeof VISIBILITY_EVENTS)[number];

export interface IdentitySessionLike {
  status: string;
}

export interface PublishInputs {
  photoUrl: string | null | undefined;
  /** The Wizytówka's „O mnie” line (tenant_profiles.profile_header). */
  profileHeader: string | null | undefined;
  /** Every identity_check_sessions row the viewer can see (any order). */
  identitySessions: ReadonlyArray<IdentitySessionLike>;
}

/**
 * Client-side APPROXIMATION of flat.is_identity_verified(user_id). Since 20261030143708 the
 * server rule is: the last pass exists (flat.verified_identities, session still approved) AND
 * the declared name still matches the verified one — which a list of session rows cannot tell.
 * This mirror over-approximates (an approved session whose name no longer matches reads as
 * verified here, not on the server). The authority for the UI is
 * `get_my_identity_status().state === 'verified'` (src/types/identity.ts); publish_my_profile()
 * remains the server's answer. Kept for the onboarding/profile surfaces until they move to the RPC.
 */
export function isIdentityVerified(sessions: ReadonlyArray<IdentitySessionLike>): boolean {
  return sessions.some((s) => s.status === 'approved');
}

/** What still blocks publishing, in server order; empty = may publish. */
export function missingPublishRequirements(inputs: PublishInputs): PublishRequirement[] {
  const missing: PublishRequirement[] = [];
  if (!(inputs.photoUrl ?? '').trim()) missing.push('photo');
  if (!(inputs.profileHeader ?? '').trim()) missing.push('bio');
  if (!isIdentityVerified(inputs.identitySessions)) missing.push('id');
  return missing;
}

/** Narrows a publish_my_profile() result (text[]) — anything unexpected is dropped, not trusted. */
export function parseMissingRequirements(raw: unknown): PublishRequirement[] {
  if (!Array.isArray(raw)) return [];
  const known = new Set<string>(PUBLISH_REQUIREMENTS);
  const out: PublishRequirement[] = [];
  for (const item of raw) {
    if (typeof item === 'string' && known.has(item) && !out.includes(item as PublishRequirement)) {
      out.push(item as PublishRequirement);
    }
  }
  return PUBLISH_REQUIREMENTS.filter((r) => out.includes(r));
}

/**
 * The genitive list after „Brakuje:” in the offline box and the profile_offline bell
 * („zdjęcia profilowego, jednego zdania o sobie, weryfikacji tożsamości”), joined with „, ”.
 */
export function missingListText(missing: readonly PublishRequirement[], t: (key: string) => string): string {
  return missing.map((m) => t(`profile_publish.missing_genitive_${m}`)).join(', ');
}

/** Server view of the caller's profile — public.get_my_profile_visibility() (20261030134633). */
export interface MyProfileVisibility {
  status: ProfileStatus;
  missing: PublishRequirement[];
  wasPublished: boolean;
}

/** Narrows one get_my_profile_visibility() row; null for no row or an unknown status. */
export function parseMyProfileVisibility(raw: unknown): MyProfileVisibility | null {
  const row = Array.isArray(raw) ? raw[0] : raw;
  if (!row || typeof row !== 'object') return null;
  const r = row as { status?: unknown; missing?: unknown; was_published?: unknown };
  if (typeof r.status !== 'string' || !(PROFILE_STATUSES as readonly string[]).includes(r.status)) return null;
  return {
    status: r.status as ProfileStatus,
    missing: parseMissingRequirements(r.missing),
    wasPublished: r.was_published === true,
  };
}

/**
 * What the publish card shows (brief 2, Maciek 30.10):
 * - `published`  — „Udostępnij” + „Ukryj profil”;
 * - `hidden`     — „Opublikuj profil” (no reason asked);
 * - `offline`    — the profile WAS live and went offline by itself: like `unpublished`, plus the
 *                  warning box below the button;
 * - `unpublished`— „Opublikuj mój profil”, dimmed (still tappable) while something is missing.
 */
export type PublishCardState = 'published' | 'hidden' | 'offline' | 'unpublished';

export function publishCardState(v: MyProfileVisibility): PublishCardState {
  if (v.status === 'published') return 'published';
  if (v.status === 'hidden') return 'hidden';
  return v.wasPublished && v.missing.length > 0 ? 'offline' : 'unpublished';
}

/**
 * True when the status turned published between two reads — the „Twój profil został opublikowany”
 * toast, manual or automatic. The first read (prev undefined) is the page load, not a change.
 */
export function becamePublished(prev: ProfileStatus | null | undefined, next: ProfileStatus | null | undefined): boolean {
  return prev !== undefined && prev !== null && prev !== 'published' && next === 'published';
}

/** The grey line under a „what's missing” item: only the identity item has one (Maciek 30.10). */
export function missingItemNoteKey(item: PublishRequirement, idInProgress: boolean): string | null {
  if (item !== 'id') return null;
  return idInProgress ? 'profile_publish.missing_item_id_pending' : 'profile_publish.missing_item_id_note';
}

/**
 * The profile_offline bell carries `missing` as an array (photo | bio | id); its body names them in
 * the genitive („Brakuje: zdjęcia profilowego, …”), the same list as the profile card's offline box.
 */
export function profileOfflineItems(payload: unknown, t: (key: string) => string): string {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return '';
  return missingListText(parseMissingRequirements((payload as Record<string, unknown>).missing), t);
}

export function isHideReason(value: unknown): value is HideReason {
  return typeof value === 'string' && (HIDE_REASONS as readonly string[]).includes(value);
}

/**
 * What the publish card shows from the last server answer and the client mirror (the fix for the
 * card flashing „unpublished” instead of „offline”). `server` is the LAST answer
 * get_my_profile_visibility() gave, kept across a change of inputs and across a failed read;
 * `fresh` says it answers the current inputs; `failed` that the read errored.
 * - A fresh answer agreeing with the local status is used as is.
 * - Otherwise the mirror's status and missing list are used, but `wasPublished` comes from the
 *   server (it only ever turns true: the history is never rewritten), so a profile that went
 *   offline keeps reading as offline while the next answer loads.
 * - With no server answer yet, the mirror is used unless it could be either offline or
 *   unpublished (status unpublished + something missing): then null — the caller shows no card
 *   rather than a wrong one — until the read answers or fails (then the mirror is the best guess).
 */
export function resolveProfileVisibility(input: {
  server: MyProfileVisibility | null;
  fresh: boolean;
  failed: boolean;
  fallback: MyProfileVisibility | null;
}): MyProfileVisibility | null {
  const { server, fresh, failed, fallback } = input;
  if (!fallback) return server;
  if (!server) {
    const ambiguous = fallback.status === 'unpublished' && fallback.missing.length > 0;
    return ambiguous && !failed ? null : fallback;
  }
  if (fresh && server.status === fallback.status) return server;
  return { ...fallback, wasPublished: fallback.wasPublished || server.wasPublished || server.status === 'published' };
}

/**
 * Which requirement an edit would take away from a LIVE profile — the trigger for
 * OfflineWarningDialog (docs/TenantIdVerification.md §9). Only 'published' profiles are warned
 * (an unpublished or hidden one is not in search, nothing goes offline). Photo first, then the
 * „O mnie” line, in server order; null = the edit takes nothing away. The server still decides.
 */
export function offlineRequirementLost(
  status: string | null | undefined,
  before: { photoUrl: string | null | undefined; profileHeader: string | null | undefined },
  after: { photoUrl: string | null | undefined; profileHeader: string | null | undefined },
): Extract<PublishRequirement, 'photo' | 'bio'> | null {
  if (status !== 'published') return null;
  const set = (v: string | null | undefined) => !!(v ?? '').trim();
  if (set(before.photoUrl) && !set(after.photoUrl)) return 'photo';
  if (set(before.profileHeader) && !set(after.profileHeader)) return 'bio';
  return null;
}
